// src/lib/emailjs-loader.js — chargement paresseux du SDK EmailJS + identifiants
// du gabarit. UNE seule implémentation (elle vivait inline dans
// App.jsx::sendFactureEmail ; l'envoi de l'avoir la réutilise).
//
// ─────────────────────────────────────────────────────────────────────────
// ⚠️ ASYMÉTRIE ASSUMÉE : CE CHEMIN N'EST PAS GATÉ, L'AUTRE L'EST
// ─────────────────────────────────────────────────────────────────────────
// Il existe DEUX façons d'envoyer un mail de facturation dans ce dépôt, et
// elles ne traitent pas `MESSAGES_DRY_RUN` de la même manière :
//
//   1. ICI — EmailJS, DANS LE NAVIGATEUR (factures + avoirs envoyés à la main
//      depuis l'écran Historique / l'onglet Facturation). Aucune variable
//      Vercel ne peut le gater : `MESSAGES_DRY_RUN` est une variable SERVEUR,
//      elle n'existe pas dans le navigateur. **Un `send` = un mail RÉEL**,
//      en toutes circonstances, y compris quand tout le reste de la prod est
//      en DRY_RUN. Le seul verrou est le clic de Dimitri (modale + case).
//
//   2. `lib/factures-envoi-handlers.js` — sender SERVEUR (envoi automatisé),
//      lui GATÉ par `MESSAGES_DRY_RUN` : en DRY_RUN il pose sa trace et
//      n'envoie rien.
//
// Conséquence à ne pas oublier : « la prod est en DRY_RUN » ne veut PAS dire
// « aucun mail de facturation ne peut partir ». Pour essayer le chemin 1 sans
// écrire à un propriétaire, il faut une adresse jetable — pas le drapeau.
// (Même nature d'asymétrie que le parcours Direct, ADR-272 : transactionnel
// non gaté ↔ temporel gaté.)
//
// Asymétrie ASSUMÉE PAR DIMITRI le 2026-09-16 (revue adverse de l'avoir) ;
// unification des deux chemins = chantier séparé, plus tard. Ne pas ajouter
// de gate ici sans décision produit : ce serait couper des envois que
// quelqu'un a explicitement demandés en cliquant.
//
// ─────────────────────────────────────────────────────────────────────────
// LES GABARITS (hébergés côté EmailJS, pas dans ce dépôt)
// ─────────────────────────────────────────────────────────────────────────
// Un gabarit EmailJS porte l'OBJET et le squelette du mail. `ncm2a7o` est
// celui des FACTURES : son objet dit « Facture N° … ». L'avoir a le sien.
//
// Décision Dimitri (2026-09-16) : « je crée le gabarit dédié chez EmailJS —
// un propriétaire qui reçoit "Facture N° AV-…", c'est faux. » L'identifiant
// vit donc dans `REACT_APP_EMAILJS_TEMPLATE_AVOIR`, et il n'y a AUCUN repli
// sur le gabarit facture : variable absente ⇒ l'envoi de l'avoir est REFUSÉ,
// et l'écran le dit. Un repli silencieux renverrait exactement le mail que
// cette décision existe pour empêcher.

export const EMAILJS_PUBLIC_KEY = "8hZqhIm1KdmjyS9lb";
export const EMAILJS_SERVICE = "service_5qsjlb9";
export const EMAILJS_TEMPLATE_FACTURE = "ncm2a7o";

/**
 * L'identifiant du gabarit DÉDIÉ à l'avoir, ou `null` s'il n'est pas
 * configuré. Jamais de repli sur EMAILJS_TEMPLATE_FACTURE.
 *
 * Lu à l'APPEL (et non figé à l'import) pour que le refus se calcule au
 * rendu de la modale, comme tout le reste de describeAvoirEnvoi.
 * ⚠️ CRA remplace `process.env.REACT_APP_*` à la compilation : la variable
 * doit être posée sur Vercel AVANT le build, pas au runtime.
 *
 * @returns {string|null}
 */
export function emailjsTemplateAvoir() {
  const v = process.env.REACT_APP_EMAILJS_TEMPLATE_AVOIR;
  return typeof v === "string" && v.trim() ? v.trim() : null;
}

const EMAILJS_SDK_URL =
  "https://cdn.jsdelivr.net/npm/@emailjs/browser@4/dist/email.min.js";

/**
 * Charge le SDK s'il ne l'est pas encore et renvoie `window.emailjs`.
 * Rejette si le script ne se charge pas (l'appelant n'envoie donc rien —
 * et, sur le chemin sendThenMarkDelivered, ne marque rien).
 */
/**
 * Un APERÇU n'envoie jamais de courriel (décision Dimitri 29/09, point 16a).
 * `REACT_APP_VERCEL_ENV` est posé par le script de construction depuis
 * `VERCEL_ENV` ; en production il vaut « production » et rien ne change —
 * l'asymétrie assumée plus haut reste vraie en production.
 */
export function apercuNavigateur(env = process.env) {
  const v = env.REACT_APP_VERCEL_ENV;
  return typeof v === "string" && v !== "" && v !== "production" && v !== "local";
}

export async function ensureEmailJs() {
  if (apercuNavigateur())
    throw new Error(
      "Aperçu : envoi de courriel désactivé. Un aperçu ne peut rien envoyer.",
    );
  if (typeof window === "undefined")
    throw new Error("EmailJS : navigateur requis");
  if (window.emailjs) return window.emailjs;
  await new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = EMAILJS_SDK_URL;
    script.onload = () => {
      if (!window.emailjs) {
        reject(new Error("EmailJS : SDK chargé mais window.emailjs absent"));
        return;
      }
      window.emailjs.init(EMAILJS_PUBLIC_KEY);
      resolve();
    };
    script.onerror = () =>
      reject(new Error("EmailJS : SDK introuvable (réseau ?)"));
    document.head.appendChild(script);
  });
  return window.emailjs;
}
